import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import LoadingState from "../../Components/LoadingState";
import { Save } from "lucide-react";
import Sidebar from "../../Components/Sidebar";
import Topbar from "../../Components/Topbar";
import Tabs from "../../Components/Tabs";
import ProfileCard from "./ProfileCard";
import WorkspaceCard from "./WorkspaceCard";
import TeamPanel from "./TeamPanel";
import NotificationsPanel from "./NotificationsPanel";
import SecurityPanel from "./SecurityPanel";
import { getAccount, updateAccount } from "../../actions/account";
import { verifyToken } from "../../actions/auth";
import {
  accountToProfile,
  accountToWorkspace,
  accountPayload,
} from "./mapAccount";

// const tabs = ["Profile", "Team", "Notifications", "Security"];
const tabs = ["Profile", "Notifications"];

// Starting values. GET /account fills these in; anything it doesn't send keeps
// the blank here rather than showing invented details.
const defaultProfile = {
  avatar: null,
  fullName: "",
  email: "",
  jobTitle: "",
  phone: "",
  timezone: "(GMT+05:30) India Standard Time",
  language: "English (US)",
};

// Stands in until GET /account lands, so the form isn't empty on a cold load.
const profileFromUser = (user) => ({
  ...defaultProfile,
  avatar: user?.avatarUrl || null,
  fullName: user?.name || "",
  email: user?.email || "",
});

const defaultWorkspace = {
  logo: null,
  name: "",
  website: "",
  industry: "",
  size: "",
  slug: "",
};

export default function AccountSettings() {
  // Put in the store by verifyToken() on boot, so the form has something to
  // show while GET /account is in flight.
  const user = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();

  const [activeTab, setActiveTab] = useState("Profile");
  const [profile, setProfile] = useState(() => profileFromUser(user));
  const [workspace, setWorkspace] = useState(defaultWorkspace);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  // What the server last confirmed. PATCH takes only the fields that changed,
  // so edits are diffed against this rather than against the defaults.
  const savedRef = useRef({ profile: null, workspace: null });
  const savedTimer = useRef(null);

  useEffect(() => () => clearTimeout(savedTimer.current), []);

  const applyAccount = useCallback((account) => {
    const nextProfile = accountToProfile(account, defaultProfile);
    const nextWorkspace = accountToWorkspace(account, defaultWorkspace);

    savedRef.current = { profile: nextProfile, workspace: nextWorkspace };
    setProfile(nextProfile);
    setWorkspace(nextWorkspace);
  }, []);

  useEffect(() => {
    let cancelled = false;

    dispatch(getAccount()).then((result) => {
      if (cancelled) return;

      setLoading(false);

      if (!result?.success) {
        setError(result?.message || "We couldn't load your account settings.");
        return;
      }

      setError("");
      applyAccount(result.account);
    });

    return () => {
      cancelled = true;
    };
  }, [dispatch, applyAccount]);

  // Only used before GET /account answers; afterwards the record is the source
  // of truth and this would fight it.
  const userName = user?.name;
  const userEmail = user?.email;
  const userAvatar = user?.avatarUrl;

  useEffect(() => {
    if (savedRef.current.profile) return;

    setProfile((prev) => ({
      ...prev,
      fullName: prev.fullName || userName || "",
      email: prev.email || userEmail || "",
      avatar: prev.avatar || userAvatar || null,
    }));
  }, [userName, userEmail, userAvatar]);

  const updateProfile = (key, value) => {
    setProfile((prev) => ({ ...prev, [key]: value }));
    setSaved(false);
  };

  const updateWorkspace = (key, value) => {
    setWorkspace((prev) => ({ ...prev, [key]: value }));
    setSaved(false);
  };

  // Back to the last saved record, not to the blank defaults.
  const handleReset = () => {
    setProfile(savedRef.current.profile || profileFromUser(user));
    setWorkspace(savedRef.current.workspace || defaultWorkspace);
    setSaved(false);
    setError("");
  };

  const handleSave = async () => {
    const payload = accountPayload(profile, workspace, savedRef.current);

    // Nothing edited: no request worth sending.
    if (!Object.keys(payload).length) {
      setSaved(true);
      return;
    }

    setSaving(true);
    setError("");

    const result = await dispatch(updateAccount(payload));

    setSaving(false);

    if (!result?.success) {
      setError(result?.message || "We couldn't save your changes.");
      return;
    }

    // Prefer the saved record the response returns; fall back to treating what
    // was sent as confirmed, so the next save doesn't resend these fields.
    if (result.account) applyAccount(result.account);
    else savedRef.current = { profile, workspace };

    // The topbar and sidebar render the name and photo from the store, which
    // only verifyToken() fills. Without this they keep the old avatar until
    // the next page load.
    dispatch(verifyToken());

    setSaved(true);
    clearTimeout(savedTimer.current);
    savedTimer.current = setTimeout(() => setSaved(false), 2500);
  };

  return (
    <div className="dash">
      <Sidebar />

      <div className="dash-main">
        <Topbar
          title="Account settings"
          subtitle="Manage your profile, workspace, team and security preferences."
        />

        <div className="dash-content">
          <Tabs tabs={tabs} active={activeTab} onChange={setActiveTab} />

          {activeTab === "Profile" &&
            (loading ? (
              <LoadingState title="Loading your account" inline />
            ) : (
              <>
                {error && <p className="auth-error">{error}</p>}

                <div className="acct-stack">
                  <ProfileCard profile={profile} onChange={updateProfile} />
                  {/* <WorkspaceCard workspace={workspace} onChange={updateWorkspace} /> */}
                </div>

                <div className="acct-action-bar">
                  <button
                    type="button"
                    className="acct-btn acct-btn--quiet"
                    onClick={handleReset}
                    disabled={saving}
                  >
                    Discard changes
                  </button>
                  <button
                    type="button"
                    className="acct-btn acct-btn--primary"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? (
                      <span className="install-status-spinner" aria-hidden="true" />
                    ) : (
                      <Save size={16} strokeWidth={2} />
                    )}
                    {saving ? "Saving..." : saved ? "Saved!" : "Save changes"}
                  </button>
                </div>
              </>
            ))}

          {activeTab === "Team" && <TeamPanel />}
          {activeTab === "Notifications" && <NotificationsPanel />}
          {activeTab === "Security" && <SecurityPanel />}
        </div>
      </div>
    </div>
  );
}
